import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ShieldCheck, ShieldOff, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { mfaAPI } from '../../lib/api';
import { SecurityAdminCards } from './SecurityAdminCards';

const OrgMfaPolicyCard = () => {
  const [policy, setPolicy] = useState(null); // {require_mfa, can_manage}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/organizations/security')
      .then(({ data }) => setPolicy(data))
      .catch(() => setPolicy(null));
  }, []);

  if (!policy?.can_manage) return null;

  const toggle = async (checked) => {
    setBusy(true);
    try {
      await api.put('/api/organizations/security', { require_mfa: checked });
      setPolicy((p) => ({ ...p, require_mfa: checked }));
      toast.success(checked
        ? 'MFA is now required for all firm users — they will be asked to enrol at next login.'
        : 'MFA requirement turned off.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update policy');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="org-mfa-policy-card">
      <CardHeader>
        <CardTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" /> Organisation Security Policy
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-foreground">Require two-factor authentication for all firm users</p>
            <p className="text-xs text-muted-foreground mt-1">
              Staff without MFA are forced to enrol at their next login, and cannot disable it while this is on.
              Required for the Xero Practice Manager security assessment (Q3.2).
            </p>
          </div>
          <Switch
            checked={!!policy.require_mfa}
            onCheckedChange={toggle}
            disabled={busy}
            data-testid="org-mfa-policy-switch"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export function SecurityTab() {
  const [status, setStatus] = useState(null);   // { enabled, eligible }
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { secret, qr }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [role, setRole] = useState(null);
  useEffect(() => { api.get('/api/me').then((r) => setRole(r.data.active_membership?.role)).catch(() => {}); }, []);

  const loadStatus = async () => {
    try {
      const { data } = await mfaAPI.status();
      setStatus(data);
    } catch {
      setStatus({ enabled: false, eligible: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await mfaAPI.setup();
      setSetupData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start MFA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const { data } = await mfaAPI.verify(code);
      setBackupCodes(data.backup_codes || null);
      toast.success('Two-factor authentication enabled');
      setSetupData(null);
      setCode('');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await mfaAPI.disable(code);
      toast.success('Two-factor authentication disabled');
      setCode('');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  // Clients (and other ineligible roles) don't get MFA.
  if (!status?.eligible) {
    return (
      <Card>
        <CardHeader><CardTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Security</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Two-factor authentication is available for firm staff accounts.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
    <OrgMfaPolicyCard />
    {backupCodes && (
      <Card data-testid="backup-codes-card"><CardHeader><CardTitle>Backup codes</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground mb-2">Each code works once if you lose your authenticator. They won't be shown again.</p>
          <div className="grid grid-cols-2 gap-1 font-mono text-sm">{backupCodes.map((c) => <span key={c}>{c}</span>)}</div>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setBackupCodes(null)}>I've saved them</Button></CardContent></Card>
    )}
    {status.enabled && (
      <Button size="sm" variant="outline" onClick={() => api.post('/api/mfa/backup-codes/regenerate').then((r) => setBackupCodes(r.data.backup_codes)).catch((e) => toast.error(e.response?.data?.detail || 'Failed'))} data-testid="regen-backup-codes-btn">Generate new backup codes</Button>
    )}
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} className="flex items-center gap-2">
          Two-Factor Authentication
          {status.enabled ? (
            <Badge className="bg-green-100 text-green-700">Enabled</Badge>
          ) : (
            <Badge className="bg-muted text-muted-foreground">Disabled</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ENABLED state */}
        {status.enabled && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-600" />
              Your account is protected. You'll enter a code from your authenticator app at login.
            </p>
            <div className="space-y-2 max-w-xs">
              <Label>Enter a current code to disable</Label>
              <Input
                inputMode="numeric" maxLength={6} placeholder="6-digit code"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="tracking-widest text-center"
              />
              <Button variant="outline" className="text-red-600" onClick={disable} disabled={busy || code.length !== 6}>
                <ShieldOff className="w-4 h-4 mr-2" /> Disable 2FA
              </Button>
            </div>
          </div>
        )}

        {/* DISABLED, not yet setting up */}
        {!status.enabled && !setupData && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Add an extra layer of security. You'll scan a QR code with an authenticator app
              (Google Authenticator, Authy) and enter a 6-digit code at login.
            </p>
            <Button className="bg-primary hover:bg-primary/90" onClick={startSetup} disabled={busy}>
              <ShieldCheck className="w-4 h-4 mr-2" /> Enable 2FA
            </Button>
          </div>
        )}

        {/* SETUP in progress — show QR + confirm */}
        {!status.enabled && setupData && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">1. Scan this QR code with your authenticator app:</p>
            <img src={setupData.qr} alt="MFA QR code" className="w-44 h-44 border rounded p-2 bg-card" />
            <p className="text-xs text-muted-foreground">
              Or enter this key manually: <code className="bg-muted px-1 rounded">{setupData.secret}</code>
            </p>
            <div className="space-y-2 max-w-xs">
              <Label>2. Enter the 6-digit code it shows</Label>
              <Input
                inputMode="numeric" maxLength={6} placeholder="6-digit code" autoFocus
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="tracking-widest text-center"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setSetupData(null); setCode(''); }} disabled={busy}>Cancel</Button>
                <Button className="bg-primary hover:bg-primary/90" onClick={confirmEnable} disabled={busy || code.length !== 6}>
                  {busy ? 'Verifying…' : 'Confirm & Enable'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    {['owner', 'admin'].includes(role) && <SecurityAdminCards role={role} />}
    </div>
  );
}
